/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import dynamic from 'next/dynamic';
import { Suspense, useEffect, useRef, useState } from 'react';
import { TrapFocus } from '@/components/Dialog/Modal/TrapFocus';
import { Drawer } from '@/components/Drawer/Drawer';
import { type AddCertonaItemObjectType } from '@/features/certona';
import { useFeatureFlag } from '@/features/globalConfig';
import { useHeaderData } from '@/features/header/api/getHeader';
import { removeDataPart } from '@/actions/shelf/shelf';
import { useDispatch } from '@/hooks/redux/useDispatch';
import { useSelector } from 'react-redux';
import { selectOrderDetailsFromState } from '@/constants/selectors';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';

const LazyMiniCartFlyoutComp = dynamic(
  () => import('@/features/miniCartFlyout').then((mod) => mod.MiniCartFlyoutComp),
  {
    loading: () => <ProgressModal noScroll />,
  }
);

type Props = {
  addedCertonaItemObject: AddCertonaItemObjectType | null;
  certonaRecommendationAddedToCart: boolean;
  setAddedCertonaItemObject: React.Dispatch<React.SetStateAction<AddCertonaItemObjectType | null>>;
};

const SearchResultsCertonaMiniCart = ({
  addedCertonaItemObject,
  certonaRecommendationAddedToCart,
  setAddedCertonaItemObject,
}: Props) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const { data: headerData } = useHeaderData();
  const isSearchPageCertonaAddToCartEnabled =
    useFeatureFlag('IS_SEARCH_PAGE_CERTONA_ADD_TO_CART_ENABLED') === 'true';
  const orderDetailsFromState = useSelector(selectOrderDetailsFromState);

  const handleCloseModal = () => {
    dispatch(removeDataPart());
    setIsModalOpen(false);
    setAddedCertonaItemObject(null);
  };

  useEffect(() => {
    if (!!addedCertonaItemObject) {
      setIsModalOpen(true);
    }
  }, [addedCertonaItemObject]);

  if (!addedCertonaItemObject) {
    return null;
  }

  return (
    <Drawer
      data-testid="search-results-certona-mini-cart"
      anchor="right"
      open={isModalOpen}
      onClose={handleCloseModal}
      drawerRef={drawerRef}
    >
      <TrapFocus open={isModalOpen}>
        <div>
          <Suspense>
            <LazyMiniCartFlyoutComp
              handleClose={handleCloseModal}
              isModalOpen={isModalOpen}
              image={addedCertonaItemObject.productImage}
              successData={addedCertonaItemObject.cartSuccessData}
              productDetails={{
                productName: addedCertonaItemObject.productDetails.productName,
                certonaItemAddedTocart:
                  certonaRecommendationAddedToCart && isSearchPageCertonaAddToCartEnabled,
                productPartType: addedCertonaItemObject.productDetails.partType,
                originalPartTypeId: addedCertonaItemObject.productDetails.partType,
                productId: addedCertonaItemObject.productDetails.productId,
                productAvailabilityInfo: {
                  aZBadgesFlagVO: null,
                  dealsInfo: undefined,
                  skuPricingAndAvailability: undefined,
                },
              }}
              miniCartData={headerData?.miniCartMap}
              closeStoreModal={handleCloseModal}
              cartUrl={addedCertonaItemObject.submitToCart}
              productTitle={addedCertonaItemObject.productTitle}
              pageName={addedCertonaItemObject.pageName}
              orderDetailsFromState={orderDetailsFromState}
              skuId={addedCertonaItemObject.productDetails.skuId}
              parentRef={drawerRef}
            />
          </Suspense>
        </div>
      </TrapFocus>
    </Drawer>
  );
};

export default SearchResultsCertonaMiniCart;
